import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { useResources } from 'api/queries/marketing-capabilities/resources/useResources'
import {
  setExploreLearningsLocation,
  setExploreLearningsOnboarding,
  setExploreLearningsOU,
  setExploreLearningsSkill,
  setExploreLearningsSkillNames,
  setExploreLearningsSort,
  useExploreLearnings,
} from 'store/ExploreLearnings'

import { EmptyResult } from '../components/EmptyResult'
import { ResourceCard } from '../components/ResourceCard'
import { ResourcesFilters } from '../components/ResourcesFilters'

export const ResourcesPage = () => {
  const { data: resourceList, isLoading, hasNextPage, fetchNextPage } = useResources()
  const onboardingState = useExploreLearnings(state => state.onboarding)
  const location = useLocation()

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const onboardingParam = params.get('onboarding')

    if (onboardingParam === 'true' && onboardingState !== 'true') {
      setExploreLearningsOnboarding('true')
    } else if (onboardingParam !== 'true' && onboardingState === 'true') {
      // Reset onboarding to false when URL doesn't have the parameter
      setExploreLearningsOnboarding('false')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const resourcesState = useExploreLearnings(state => state.resources)
  const ouState = useExploreLearnings(state => state.OU)
  const skillState = useExploreLearnings(state => state.skill)

  const handleApplyFilters = (onboarding: string, ous: string[], skills: string[]) => {
    setExploreLearningsOU(ous)
    setExploreLearningsSkill(skills)
    setExploreLearningsOnboarding(onboarding)
  }

  const handleClearAllFilters = () => {
    setExploreLearningsOU([])
    setExploreLearningsSkill([])
    setExploreLearningsSkillNames([])
    setExploreLearningsOnboarding('false')
    setExploreLearningsLocation([])
  }

  const resources = !isLoading && resourceList && resourceList.pages.flat()
  const count = () => resourceList?.meta.total || 0
  const search = useExploreLearnings(state => state.search)

  return (
    <>
      {(!search || count() > 0) && (
        <ResourcesFilters
          resourcesState={resourcesState}
          ouState={ouState}
          skillState={skillState}
          onboardingState={onboardingState}
          onApplyFilters={handleApplyFilters}
          onClearAllFilters={handleClearAllFilters}
          onSort={setExploreLearningsSort}
        />
      )}
      {search && resources && resources.length ? (
        <div className="w-[1120px] mx-auto">
          <p className="text-[16px] leading-[24px] font-TCCCText mt-2 mb-[16px]">
            <b className="text-[20px] font-semibold leading-[32px]">{count()}</b> resource
            {count() > 1 ? 's' : ''} matching with "{search}"
          </p>
        </div>
      ) : (
        ''
      )}
      {resources && resources.length && !isLoading ? (
        <div className="w-[1120px] pb-8 mx-auto flex flex-wrap gap-x-8 gap-y-8">
          {resources.map((resource: any) => (
            <ResourceCard
              key={resource.id}
              title={resource.name}
              link={resource.url}
              description={resource.description}
              skills={resource.skills}
              timeToComplete={resource.readTime}
              onboarding={resource.onboarding}
            />
          ))}
        </div>
      ) : (
        ''
      )}
      {hasNextPage && count() > 0 && (
        <button className="w-full mx-auto text-blue-500 font-bold font-TCCC mb-10" onClick={() => fetchNextPage()}>
          See more
        </button>
      )}
      {count() === 0 && !isLoading && <EmptyResult />}
      {resourceList &&
        resourceList?.exploreMoreResources &&
        resourceList?.exploreMoreResources.length > 0 &&
        !isLoading && (
          <div className="w-[1120px] pb-16 mx-auto flex flex-col border-t border-solid border-[#D7D7D7] pt-10">
            <div className="flex justify-between mb-6">
              <p className="heading-2xl">Explore resources</p>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-6">
              {resourceList?.exploreMoreResources.map((resource: any) => (
                <ResourceCard
                  key={resource.id}
                  title={resource.name}
                  link={resource.url}
                  description={resource.description}
                  skills={resource.skills}
                  timeToComplete={resource.readTime}
                />
              ))}
            </div>
          </div>
        )}
    </>
  )
}
